import { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Loader2, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STAMP_KEY = 'oe_money_stamp';
const STAMP_TS_KEY = 'oe_money_stamp_ts';
const TTL_MS = 15 * 60 * 1000; // 15 minutes

export function getMoneyStamp() {
  const stamp = sessionStorage.getItem(STAMP_KEY);
  const ts = parseInt(sessionStorage.getItem(STAMP_TS_KEY) || '0', 10);
  if (!stamp) return null;
  if (Date.now() - ts > TTL_MS) {
    sessionStorage.removeItem(STAMP_KEY);
    sessionStorage.removeItem(STAMP_TS_KEY);
    return null;
  }
  return stamp;
}

export function clearMoneyStamp() {
  sessionStorage.removeItem(STAMP_KEY);
  sessionStorage.removeItem(STAMP_TS_KEY);
}

/** Wrap any money-displaying content. Shows a PIN entry until verified. */
export default function MoneyPinGate({ children, title = 'Reveal Money' }) {
  const [unlocked, setUnlocked] = useState(!!getMoneyStamp());
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (!unlocked) {
      axios.get(`${API}/api/admin/money-pin/status`, { withCredentials: true })
        .then(r => setIsDefault(r.data.is_default))
        .catch(() => {});
    }
  }, [unlocked]);

  const submit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (pin.length !== 4 || !/^\d{4}$/.test(pin)) {
      setError('PIN must be 4 digits');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/money-pin/verify`, { pin }, { withCredentials: true });
      sessionStorage.setItem(STAMP_KEY, data.stamp);
      sessionStorage.setItem(STAMP_TS_KEY, String(Date.now()));
      window.dispatchEvent(new CustomEvent('oe-money-unlocked'));
      setUnlocked(true);
      setPin('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Verification failed');
    }
    setSubmitting(false);
  };

  if (unlocked) {
    return (
      <div>
        {children}
        <button
          onClick={() => { clearMoneyStamp(); setUnlocked(false); }}
          data-testid="money-lock-btn"
          className="mt-4 flex items-center gap-1.5 text-[10px] font-heading tracking-widest uppercase text-muted-foreground hover:text-red-400 transition-colors"
        >
          <Lock size={11} /> Lock money view
        </button>
      </div>
    );
  }

  return (
    <div className="border-2 border-yellow-700/40 bg-yellow-900/5 rounded-xl p-8 max-w-md mx-auto text-center" data-testid="money-pin-gate">
      <div className="w-14 h-14 rounded-full bg-yellow-500/10 mx-auto mb-4 flex items-center justify-center">
        <KeyRound size={24} className="text-yellow-500" />
      </div>
      <h3 className="font-heading text-base tracking-widest uppercase mb-1">{title}</h3>
      <p className="font-sans text-xs text-muted-foreground mb-5">
        Enter your 4-digit money PIN to view revenue.<br />
        <span className="text-[10px]">Unlocks for 15 minutes.</span>
      </p>
      <form onSubmit={submit} className="space-y-3">
        <input
          autoFocus
          type="password"
          inputMode="numeric"
          maxLength={4}
          value={pin}
          onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          placeholder="••••"
          data-testid="money-pin-input"
          className="w-32 text-center font-mono text-3xl tracking-[0.5em] bg-background border-2 border-border rounded-xl py-3 focus:outline-none focus:border-yellow-500 placeholder-muted-foreground"
        />
        {error && <p className="font-sans text-xs text-red-400" data-testid="money-pin-error">{error}</p>}
        <div>
          <button
            type="submit"
            disabled={submitting || pin.length !== 4}
            data-testid="money-pin-submit"
            className="font-heading text-xs tracking-widest uppercase bg-yellow-500 text-black px-6 py-2.5 rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-40"
          >
            {submitting ? <Loader2 size={13} className="inline animate-spin" /> : 'Unlock'}
          </button>
        </div>
        {isDefault && (
          <p className="font-sans text-[10px] text-yellow-500/80 mt-2">
            Hint: default PIN is <strong>2026</strong>. Change it in Settings → Money PIN.
          </p>
        )}
      </form>
    </div>
  );
}
